import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Building2, CheckCircle2, MapPin, Users } from "lucide-react";
import { HUBS, getHub, type Hub } from "@/data/hubs";
import { ogImageMeta } from "@/lib/og-image";
import planoImg from "@/assets/corporate-lobby-hero-3-vending.png.asset.json";
import friscoImg from "@/assets/corporate-micro-market-4-bay.png.asset.json";
import lasColinasImg from "@/assets/corporate-kiosk-5-bay-micro-market.png.asset.json";
import siliconBeachImg from "@/assets/urban-fuel-smart-vending-office-lobby.png.asset.json";
import dtlaImg from "@/assets/four-vending-machines-lineup-usa.png.asset.json";
import siliconValleyImg from "@/assets/smart-fridge-freezer-24h-corporate.png.asset.json";
import somaImg from "@/assets/red-vending-machines-council-foyer.png.asset.json";

const HUB_IMAGES: Record<string, { url: string; alt: string }> = {
  "plano-legacy-business-park": {
    url: planoImg.url,
    alt: "Free vending machines in a corporate lobby — Legacy Business Park, Plano TX",
  },
  "frisco-platinum-corridor": {
    url: friscoImg.url,
    alt: "Free micro-market fresh food and drinks bay — Frisco Platinum Corridor, TX",
  },
  "las-colinas-irving": {
    url: lasColinasImg.url,
    alt: "Corporate 5-bay kiosk micro-market — Las Colinas, Irving TX",
  },
  "silicon-beach": {
    url: siliconBeachImg.url,
    alt: "Smart vending machine in a modern tech office lobby — Silicon Beach, LA",
  },
  "downtown-los-angeles": {
    url: dtlaImg.url,
    alt: "Full lineup of snack, drink and combo vending machines — Downtown LA towers",
  },
  "silicon-valley": {
    url: siliconValleyImg.url,
    alt: "AI-powered smart fridge and freezer for 24/7 tech offices — Silicon Valley",
  },
  "san-francisco-soma-financial-district": {
    url: somaImg.url,
    alt: "Combo snack, coffee and drink vending machines in an SF FiDi lobby",
  },
};

export const Route = createFileRoute("/hubs/$hub")({
  loader: ({ params }) => {
    const hub = getHub(params.hub);
    if (!hub) throw notFound();
    return { hub };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { hub } = loaderData;
    const url = `https://vendingmachinefree.com/hubs/${hub.slug}`;
    const title = `${hub.name} — Free Vending & Micro-Markets | ${hub.city}`;
    const desc = `Free vending machines, smart fridges, and micro-markets for corporate & tech offices inside ${hub.name}. $0 install, $0 service, no contracts. Serving ${hub.city}.`;
    const img = HUB_IMAGES[hub.slug];
    const ogImg = img ? `https://vendingmachinefree.com${img.url}` : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(ogImg
          ? [
              { property: "og:image", content: ogImg },
              { property: "og:image:alt", content: img!.alt },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: ogImg },
            ]
          : ogImageMeta()),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Vending Machine Free.com — ${hub.name}`,
              areaServed: { "@type": "Place", name: hub.name },
              description: desc,
              url,
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "Free Vending Machine & Micro-Market Placement",
              provider: { "@type": "Organization", name: "Vending Machine Free.com" },
              areaServed: { "@type": "Place", name: hub.name },
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: hub.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                { "@type": "ListItem", position: 2, name: "Hubs", item: "https://vendingmachinefree.com/hubs" },
                { "@type": "ListItem", position: 3, name: hub.name, item: url },
              ],
            },
          ]),
        },
      ],
    };
  },
  component: HubPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <Link to="/hubs" className="text-safety">Back to hubs</Link>
    </div>
  ),
  errorComponent: () => <div className="p-10">Error.</div>,
});

function HubPage() {
  const { hub } = Route.useLoaderData();
  const otherHubs = HUBS.filter((h) => h.slug !== hub.slug);
  const heroImg = HUB_IMAGES[hub.slug];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center">
          <div className="min-w-0">
            <nav className="text-xs text-white/60 mb-4">
              <Link to="/" className="hover:text-safety">Home</Link> /{" "}
              <Link to="/hubs" className="hover:text-safety">Hubs</Link> /{" "}
              <span className="text-white">{hub.name}</span>
            </nav>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
              <Building2 className="h-4 w-4" /> {hub.region}
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
              Free Vending & Micro-Markets — {hub.name}
            </h1>
            <p className="mt-4 text-white/80 max-w-3xl text-lg">{hub.intro}</p>
            <Link
              to="/"
              hash="contact"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
            >
              Get a Free Placement in {hub.city} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {heroImg && (
            <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black/20">
              <img
                src={heroImg.url}
                alt={heroImg.alt}
                width={1200}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          )}
        </div>
      </section>



      <section className="py-14">
        <div className="container-x max-w-5xl grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-2xl uppercase">Ideal Tenants in {hub.name}</h2>
            <ul className="mt-4 space-y-2">
              {hub.targetTenants.map((t: string) => (
                <li key={t} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase">Areas We Serve</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {hub.neighborhoods.map((n: string) => (
                <span key={n} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-safety" /> {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-secondary">
        <div className="container-x max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <Users className="h-4 w-4" /> Micro-Market Fit
          </div>
          <h2 className="mt-3 font-display text-3xl uppercase">Why Micro-Markets Win Here</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{hub.microMarketAngle}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/free-micro-market-placement" className="text-sm font-semibold text-safety hover:underline">
              Learn about free micro-market placement →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">FAQs — {hub.name}</h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {hub.faqs.map(({ q, a }: { q: string; a: string }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-secondary text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Apply for a Free Placement in {hub.city}</h2>
          <p className="mt-3 text-muted-foreground">Local operator response in 24 hours.</p>
          <Link
            to="/"
            hash="contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Apply Free — {hub.city} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-6xl">
          <h3 className="font-display text-xl uppercase text-center">Explore Other Hubs</h3>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            {otherHubs.map((h: Hub) => (
              <Link
                key={h.slug}
                to="/hubs/$hub"
                params={{ hub: h.slug }}
                className="rounded-lg border border-border bg-card p-4 hover:border-safety transition"
              >
                <div className="text-xs text-muted-foreground">{h.city}</div>
                <div className="mt-1 font-semibold text-sm">{h.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
